import {
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Typography, CircularProgress, Alert,
} from '@mui/material'
import { t } from '@lingui/macro'
import type { ThresholdAlerts } from '@/core/api/statistics'

type Props = {
  data?: ThresholdAlerts[]
  isLoading: boolean
  error?: unknown
  multiEstablishment: boolean
}

export function ThresholdAlertsTable({ data, isLoading, error, multiEstablishment }: Props) {
  if (isLoading) return <CircularProgress size={24} />
  if (error) return <Alert severity="error">{t`Error loading alerts`}</Alert>
  if (!data || data.length === 0) return <Typography color="text.secondary">{t`No data available`}</Typography>

  const ALERT_LABELS: { key: keyof Omit<ThresholdAlerts, 'establishmentId' | 'establishmentName'>; label: string }[] = [
    { key: 'highGlycemia', label: t`Blood sugar ≥ 1 g/L` },
    { key: 'highBloodPressure', label: t`Blood pressure ≥ 140/90 mmHg` },
    { key: 'highBPOver40', label: t`High BP in patients ≥ 40 years` },
    { key: 'noECGOver40', label: t`Patients ≥ 40 without ECG (12 months)` },
  ]

  const establishments = data.sort((a, b) => a.establishmentName.localeCompare(b.establishmentName))

  if (!multiEstablishment) {
    const singleEst = establishments[0]
    return (
      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow sx={{ backgroundColor: 'primary.main' }}>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>{t`Alert`}</TableCell>
              <TableCell align="center" sx={{ color: 'white', fontWeight: 'bold' }}>{t`Count`}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {ALERT_LABELS.map(({ key, label }) => (
              <TableRow key={key} hover>
                <TableCell sx={{ fontWeight: 500 }}>{label}</TableCell>
                <TableCell align="center">{singleEst?.[key] as number ?? 0}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    )
  }

  return (
    <TableContainer component={Paper} variant="outlined">
      <Table size="small">
        <TableHead>
          <TableRow sx={{ backgroundColor: 'primary.main' }}>
            <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>{t`Alert`}</TableCell>
            {establishments.map(est => (
              <TableCell key={est.establishmentId} align="center" sx={{ color: 'white', fontWeight: 'bold' }}>
                {est.establishmentName}
              </TableCell>
            ))}
            <TableCell align="center" sx={{ color: 'white', fontWeight: 'bold' }}>{t`Total`}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {ALERT_LABELS.map(({ key, label }) => {
            const total = establishments.reduce((sum, est) => sum + (est[key] as number), 0)
            return (
              <TableRow key={key} hover>
                <TableCell sx={{ fontWeight: 500 }}>{label}</TableCell>
                {establishments.map(est => (
                  <TableCell key={est.establishmentId} align="center">
                    {est[key] as number}
                  </TableCell>
                ))}
                <TableCell align="center" sx={{ fontWeight: 'bold' }}>{total}</TableCell>
              </TableRow>
            )
          })}
        </TableBody>
      </Table>
    </TableContainer>
  )
}
